'use client';

import { createContext, useContext, useEffect, useState } from 'react';

const TOKEN_KEY = 'cd_token';

interface SessionUser {
  id: string;
  role: string;
  mobile: string;
  name: string;
  realEmail?: string;
  companyName?: string;
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function parseJwtPayload(token: string): Omit<SessionUser, 'realEmail' | 'companyName'> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      Buffer.from(
        parts[1].replace(/-/g, '+').replace(/_/g, '/'),
        'base64' ).toString('utf8')
    );
    // Check expiry
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) return null;
    if (!payload.userId || !payload.role) return null;
    return {
      id: payload.userId,
      role: payload.role,
      mobile: payload.mobile || '',
      name: payload.name || '',
    };
  } catch {
    return null;
  }
}

function readTokenFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAndMergeProfile = async (base: Omit<SessionUser, 'realEmail' | 'companyName'>) => {
    try {
      const token = readTokenFromStorage();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch('/api/auth/me', { credentials: 'include', headers });
      if (res.ok) {
        const data = await res.json();
        if (data?.user) {
          setSessionUser({
            ...base,
            name: data.user.name || base.name,
            realEmail: data.user.email || undefined,
            companyName: data.user.company_name || undefined,
          });
          return;
        }
      }
    } catch {
      // ignore — fall through to base
    }
    setSessionUser(base);
  };

  useEffect(() => {
    const token = readTokenFromStorage();
    if (token) {
      const parsed = parseJwtPayload(token);
      if (parsed) {
        // Set base immediately so UI doesn't flash loading
        setSessionUser(parsed);
        setLoading(false);
        // Then enrich with real email from DB
        fetchAndMergeProfile(parsed);
        return;
      }
    }
    setSessionUser(null);
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshProfile = async () => {
    if (!sessionUser) return;
    await fetchAndMergeProfile(sessionUser);
  };

  // Expose a user object that matches the shape the rest of the app expects
  const user = sessionUser
    ? {
        id: sessionUser.id,
        // Use real email from DB if available, otherwise fall back to internal
        email: sessionUser.realEmail || `${sessionUser.mobile}@challandesk.internal`,
        user_metadata: {
          full_name: sessionUser.name || sessionUser.mobile,
          role: sessionUser.role,
          phone: sessionUser.mobile,
          company_name: sessionUser.companyName || '',
        },
        email_confirmed_at: new Date().toISOString(),
      }
    : null;

  // Legacy signIn shim — not used in OTP flow but kept for compatibility
  const signIn = async (_email: string, _password: string) => {
    throw new Error('Use OTP login instead.');
  };

  // Legacy signUp shim
  const signUp = async (_email: string, _password: string, _metadata: any = {}) => {
    throw new Error('Use OTP registration instead.');
  };

  // Sign Out — clears localStorage token and redirects to login
  const signOut = async () => {
    try {
      await fetch('/api/auth/signout', { method: 'POST', credentials: 'include' });
    } catch {
      // ignore
    }

    setSessionUser(null);

    // Clear all client-side storage
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    try { localStorage.clear(); } catch { /* ignore */ }

    // Hard redirect
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  };

  const getCurrentUser = async () => user;

  const isEmailVerified = () => !!sessionUser;

  const getUserProfile = async () => {
    if (!sessionUser) return null;
    return {
      id: sessionUser.id,
      role: sessionUser.role,
      mobile: sessionUser.mobile,
      name: sessionUser.name,
    };
  };

  const value: AuthContextType = {
    user,
    session: sessionUser ? { user } : null,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
